import { lazy } from 'react';

import { CacheKey } from '../common/constants';
import reloadDocument from '../common/reloadDocument';
import { hasUnsavedChanges } from './hooks/useRefreshWarning';

const readMark = (): boolean => {
  try {
    return sessionStorage.getItem(CacheKey.ChunkReloaded) !== null;
  } catch {
    // Unreadable storage cannot bound a reload, so treat it as already spent
    return true;
  }
};

const writeMark = (): boolean => {
  try {
    sessionStorage.setItem(CacheKey.ChunkReloaded, 'true');
    return true;
  } catch {
    return false;
  }
};

const clearMark = (): void => {
  try {
    sessionStorage.removeItem(CacheKey.ChunkReloaded);
  } catch {
    // A mark left behind only costs one later recovery
  }
};

// Read here, not at the failure: a page that loads first clears the mark
const canReload = !readMark();

// A reload only queues navigation, so a chunk landing after must not clear it
let reloadTriggered = false;

/**
 * `React.lazy` that reloads the page once when a chunk will not load.
 *
 * Chunk names are content-hashed, so a page held across a deploy can ask for
 * files that are gone. Every rejection is treated as that case: a missing chunk
 * redirects to the app document, so it fails parsing HTML and cannot be told
 * apart from any other failure.
 */
const lazyWithReload: typeof lazy = factory =>
  lazy(() =>
    factory().then(
      module => {
        if (!reloadTriggered) {
          clearMark();
        }
        return module;
      },
      (error: unknown) => {
        if (
          !canReload ||
          reloadTriggered ||
          !navigator.onLine ||
          hasUnsavedChanges() ||
          !writeMark()
        ) {
          throw error;
        }

        reloadTriggered = true;
        reloadDocument();

        // Never settles, so the fallback stays up while the page reloads
        return new Promise<never>(() => {});
      }
    )
  );

export default lazyWithReload;
